import CloseIcon from "@mui/icons-material/Close"
import {Dialog, IconButton, LinearProgress, Snackbar} from "@mui/material"
import {IPFS} from "ipfs-core"
import Identities from "orbit-db-identity-provider"
import React, {useCallback, useContext, useEffect, useRef, useState} from "react"
import {AppConfig} from "../../../App.config"
import {AuthContext} from "../../../context/Auth.context"
import {BackendContext} from "../../../context/BackendContext"
import {ProfileContext} from "../../../context/Profile.context"
import {UIHelperContext} from "../../../context/UIHelper.context"
import {notifyUpdate} from "../../../functions/ipfs-orbit"
import {useAPI} from "../../../hooks/useAPI"
import {useFetchProfile} from "../../../hooks/useFetchProfile"
import {Keys} from "../../../interfaces/keys"
import {ProfileDB} from "../../../models/ProfileDB.model"
import styles from "./index.module.scss"

interface AppContainerProps {
  children: JSX.Element,
  isInit: boolean
}

export const AppContainer = ({children, isInit}: AppContainerProps) => {
  const {ipfs} = useContext(BackendContext)
  const {keys} = useContext(AuthContext)
  const {profile, profileReplCnt, setProfileReplCnt} = useFetchProfile()
  const [showProgressBar, setShowProgressBar] = useState(false)
  const [showSnack, setShowSnack] = useState(false)
  const [snackMessage, setSnackMessage] = useState("")
  const [promptSetName, setPromptSetName] = useState(false)
  const {sync, healthCheck} = useAPI()
  const nameInput = useRef<HTMLInputElement>(null)

  const updateProfileDB = useCallback(async (ipfs: IPFS, keys: Keys, profile: ProfileDB) => {
    const webIdentity = await Identities.createIdentity({
      type: "Ed25519IdentityProvider",
      keypair: keys.webIpfs
    })
    const desktopIdentity = await Identities.createIdentity({
      type: "Ed25519IdentityProvider",
      keypair: keys.desktopIpfs
    })
    await profile.set("webIdentity", webIdentity.id)
    await profile.set("desktopIdentity", desktopIdentity.id)
    await notifyUpdate(ipfs, profile.address.toString())
  }, [])

  const syncToLocal = useCallback(async (keys: Keys, profile: ProfileDB) => {
    const healthy = await healthCheck()
    const name = profile.get("name")
    if (!healthy || !name) {
      return
    }
    await sync(keys, profile.address.toString(), name)
  }, [sync, healthCheck])

  // Add web identity and desktop identity to profile
  useEffect(() => {
    if (!keys || !profile || !ipfs) {
      return
    }
    updateProfileDB(ipfs, keys, profile).then()
  }, [keys, profile, ipfs, updateProfileDB])

  // Check if name not set
  useEffect(() => {
    if (!profile || !keys) {
      return
    }
    const name = profile.get("name")
    if (!name) {
      setPromptSetName(true)
    } else {
      setPromptSetName(false)
      syncToLocal(keys, profile).then()
    }
  }, [profile, profileReplCnt, syncToLocal, keys])

  const onCloseSnack = useCallback(
    (event: any, reason: any) => {
      if (reason === "clickaway") {
        return
      }
      setShowSnack(false)
      setSnackMessage("")
    }, []
  )

  const onClickSnackClose = useCallback(
    () => {
      setShowSnack(false)
      setSnackMessage("")
    }, []
  )

  const onConfirmName = useCallback(async () => {
    if (!profile || !ipfs) {
      return
    }

    const name = nameInput.current?.value
    if (!name) {
      setSnackMessage("Name cannot be empty")
      return
    }

    await profile.set("name", name)
    await notifyUpdate(ipfs, profile.address.toString())

    setPromptSetName(false)
  }, [profile, ipfs])

  useEffect(() => {
    setShowProgressBar(!isInit)
  }, [isInit])

  useEffect(() => {
    if (!snackMessage) {
      return
    }
    setShowSnack(true)
  }, [snackMessage])

  return (
    <ProfileContext.Provider value={{profile, profileReplCnt, setProfileReplCnt}}>
      <UIHelperContext.Provider value={{
        showProgressBar,
        setShowProgressBar,
        showSnack,
        setShowSnack,
        snackMessage,
        setSnackMessage,
      }}>
        {children}

        <Snackbar
          open={showSnack}
          autoHideDuration={AppConfig.defaultSnackBarTime}
          onClose={onCloseSnack}
          message={snackMessage}
          action={(
            <IconButton onClick={onClickSnackClose}>
              <CloseIcon className={styles.CloseSnackButton} fontSize="small"/>
            </IconButton>
          )}
        />

        <div className={styles.ProgressBar} hidden={!showProgressBar}>
          <LinearProgress classes={{
            colorPrimary: styles.ColorPrimary,
            barColorPrimary: styles.BarColorPrimary
          }}/>
        </div>

        <Dialog open={promptSetName}
                PaperProps={{className: styles.NameInputDialog}}
        >
          <div className={styles.Header}>
            Just one step left...
          </div>
          <div className={styles.Desc}>
            Input your name to complete the setup. This cannot be changed later.
          </div>
          <input className={styles.NameInput}
                 placeholder={"Your name"}
                 ref={nameInput}
          />
          <button className={styles.ConfirmBtn}
                  onClick={onConfirmName}
          >
            Confirm
          </button>
        </Dialog>

      </UIHelperContext.Provider>
    </ProfileContext.Provider>
  )
}
